import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, CreditCard, ArrowRight, Home, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import axios from "axios";



const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const transactionId = searchParams.get("transaction_id");
  const [status, setStatus] = useState("loading"); // loading, success, authorized, failed
  const [transaction, setTransaction] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (transactionId) {
      fetchTransaction();
    } else if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, transactionId]);

  const fetchTransaction = async () => {
    try {
      const response = await axios.get(`${API}/payments/transaction/${transactionId}`);
      setTransaction(response.data);
      const ps = response.data.payment_status;
      if (ps === "authorized") setStatus("authorized");
      else if (ps === "paid" || ps === "captured") setStatus("success");
      else setStatus("failed");
    } catch (error) {
      console.error("Error fetching transaction:", error);
      setStatus("failed");
    }
  };

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;
      setTransaction(data);

      if (data.payment_status === "authorized") {
        setStatus("authorized");
        return;
      } else if (data.payment_status === "paid" || data.payment_status === "captured") {
        setStatus("success");
        return;
      } else if (data.payment_status === "failed") {
        setStatus("failed");
        return;
      }

      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <section className="pt-32 pb-20">
        <div className="section-container">
          <div className="max-w-lg mx-auto">
            {/* Loading State */}
            {status === "loading" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-8 text-center"
              >
                <div className="w-20 h-20 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-lime-600 animate-spin" />
                </div>
                <h1 className="heading-3 text-emerald-950 mb-4">Processing Payment</h1>
                <p className="text-stone-600 mb-4">
                  Please wait while we confirm your payment...
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-stone-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying with payment provider
                </div>
              </motion.div>
            )}

            {/* Authorized State (Pre-auth hold) */}
            {status === "authorized" && transaction && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-card p-8 text-center"
                data-testid="payment-authorized-card"
              >
                <div className="w-20 h-20 bg-lime-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-10 h-10 text-emerald-950" />
                </div>
                <h1 className="heading-3 text-emerald-950 mb-4">Payment Authorized!</h1>
                <p className="text-stone-600 mb-4">
                  A temporary hold of <strong>${transaction.amount?.toFixed(2)} CAD</strong> has been placed on your card.
                </p>
                <p className="text-sm text-stone-500 mb-8">
                  Finding your driver now. You'll only be charged once the service is completed.
                </p>

                <div className="bg-emerald-950 rounded-2xl p-6 mb-8 text-left">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-5 h-5 text-lime-400" />
                    <span className="text-white font-medium">Authorization Details</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-400">Status</span>
                      <span className="text-lime-400 font-semibold">Hold Placed</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-400">Service</span>
                      <span className="text-white capitalize">{transaction.booking_type}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-400">Amount Held</span>
                      <span className="text-lime-400 font-bold">${transaction.amount?.toFixed(2)} CAD</span>
                    </div>
                    <div className="border-t border-emerald-800 pt-3 mt-3">
                      <p className="text-xs text-stone-400">
                        This is a temporary hold, not a charge. The final amount will be captured only after service completion.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <Link to="/my-bookings">
                    <Button className="btn-primary w-full" data-testid="view-bookings-btn">
                      View My Bookings
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="w-full" data-testid="back-home-btn">
                      <Home className="w-4 h-4 mr-2" />
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}

            {/* Success State */}
            {status === "success" && transaction && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-card p-8 text-center"
              >
                <div className="w-20 h-20 bg-lime-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-emerald-950" />
                </div>
                <h1 className="heading-3 text-emerald-950 mb-4">Payment Successful!</h1>
                <p className="text-stone-600 mb-8">
                  Your {transaction.booking_type} booking has been confirmed and paid.
                </p>

                {/* Payment Details */}
                <div className="bg-emerald-950 rounded-2xl p-6 mb-8 text-left">
                  <div className="flex items-center gap-3 mb-4">
                    <CreditCard className="w-5 h-5 text-lime-400" />
                    <span className="text-white font-medium">Payment Details</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-400">Transaction ID</span>
                      <span className="text-white font-mono text-xs">{transaction.transaction_id?.slice(0, 8)}...</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-400">Service</span>
                      <span className="text-white capitalize">{transaction.booking_type}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-400">Amount Paid</span>
                      <span className="text-lime-400 font-bold">${transaction.amount?.toFixed(2)} CAD</span>
                    </div>
                    <div className="border-t border-emerald-800 pt-3 mt-3">
                      <div className="flex justify-between text-xs">
                        <span className="text-stone-500">Platform Fee (20%)</span>
                        <span className="text-stone-400">${transaction.platform_fee?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-stone-500">Provider Receives</span>
                        <span className="text-stone-400">${transaction.provider_amount?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-stone-500 mb-6">
                  A confirmation email has been sent to {transaction.customer_email}
                </p>

                <div className="flex flex-col gap-3">
                  <Link to="/">
                    <Button className="btn-primary w-full">
                      <Home className="w-4 h-4 mr-2" />
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}

            {/* Failed State */}
            {status === "failed" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-card p-8 text-center"
              >
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h1 className="heading-3 text-emerald-950 mb-4">Payment Failed</h1>
                <p className="text-stone-600 mb-8">
                  We could not confirm your payment. Please try again or contact support.
                </p>

                <div className="flex flex-col gap-3">
                  <Link to="/">
                    <Button className="btn-primary w-full">
                      Try Again
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <p className="text-sm text-stone-500">
                    Need help? Contact us at 905-431-0103
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
